<template>
  <component
    :is="radioButtonWrap"
    :gap="gap"
    ref="radioButtonWrapRef"
  >
    <KkOptionItem
      :id="id"
      :input-position="radioPosition"
      :icon-position="optionIconPosition"
      :size="radioSize"
      :label="label"
      :description="description"
      :icon-name="iconName"
      :is-disabled="disabled"
      :image-src="imageSrc"
      :image-alt="imageAlt"
    >
      <template #input>
        <div
          :class="[
            'kk-radio-button-item__radio-button',
            `kk-radio-button-item__radio-button--${radioSize}`,
            `kk-radio-button-item__radio-button--${radioPosition}`,
          ]"
        >
          <KkRadioButton
            :id="id"
            :ref="id"
            v-model="model"
            :input-value="inputValue"
            :is-disabled="disabled"
          />
        </div>
      </template>
      <template
        v-if="$slots.footer"
        #footer
      >
        <slot name="footer" />
      </template>
    </KkOptionItem>
    <div
      v-if="$slots.collapse"
      v-show="isChecked"
      class="kk-radio-button-collapse"
      :class="{
        'kk-radio-button-collapse__open': isChecked,
      }"
    >
      <slot name="collapse" />
    </div>
  </component>
</template>

<script>
/**
 * @import { Ref } from "vue";
 */

import { computed, getCurrentInstance, ref, useSlots } from 'vue';
import { uniqueId } from 'lodash-es';

import {
  ICON_POSITION_LIST,
  RADIO_BUTTON_ITEM_SIZE_LIST,
} from '../../../constants/KkRadioButtonGroup.constants';
import { useInjectRadioButtonGroupContext } from '../../hooks/useRadioButtonGroupContext';
import LayoutList from '../../../layouts/LayoutList.vue';
import KkOptionItem from '../../KkOptionItem';
import KkRadioButton from '../../KkRadioButton';

export default {
  name: 'KkRadioButtonItem',
  components: {
    LayoutList,
    KkOptionItem,
    KkRadioButton,
  },
  props: {
    value: {
      type: [String, Number, Boolean],
      default: null,
    },
    inputValue: {
      type: [String, Number, Boolean],
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: '',
    },
    radioPosition: {
      type: String,
      default: 'left',
    },
    size: {
      type: String,
      default: 'md',
      validator: (val) => RADIO_BUTTON_ITEM_SIZE_LIST.indexOf(val) !== -1,
    },
    iconName: {
      type: String,
      default: '',
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator: (val) => ICON_POSITION_LIST.indexOf(val) !== -1,
    },
    imageSrc: {
      type: String,
      default: '',
    },
    imageAlt: {
      type: String,
      default: '',
    },
    gap: {
      type: [String, Number],
      default: 3,
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const radioButtonWrapRef = ref(null);
    /** @type {Ref<Vue | null>} */
    const radioGroup = ref(null);

    const radioButtonGroup = useInjectRadioButtonGroupContext();
    const slots = useSlots();

    const id = computed(() => {
      return uniqueId('kk-radio-button-item');
    });

    const isGroup = computed(() => {
      const instance = getCurrentInstance();
      let parent = instance?.proxy?.$parent;

      while (parent) {
        if (parent.$options.name !== 'KkRadioButtonGroup') {
          parent = parent.$parent;
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          radioGroup.value = parent;
          return true;
        }
      }

      return false;
    });

    const model = computed({
      get() {
        return radioButtonGroup?.value.value ?? props.value;
      },
      set(val) {
        if (isGroup.value) {
          radioButtonGroup.onChange?.(val);
        } else {
          emit('input', val);
        }
      },
    });

    const radioSize = computed(() => {
      if (isGroup.value) {
        return radioButtonGroup?.size?.value;
      }

      return props.size;
    });

    const disabled = computed(() => {
      if (isGroup.value) {
        return radioButtonGroup?.isDisabled?.value;
      }

      return props.isDisabled;
    });

    const isChecked = computed(() => {
      return props.inputValue === model.value;
    });

    const optionIconPosition = computed(() => {
      switch (props.radioPosition) {
        case 'right':
          return props.iconPosition;
        case 'left':
        default:
          return 'top-right';
      }
    });

    const radioButtonWrap = computed(() => {
      return slots.collapse ? 'LayoutList' : 'div';
    });

    return {
      id,
      radioSize,
      disabled,
      model,
      radioButtonWrapRef,
      isGroup,
      radioGroup,
      isChecked,
      optionIconPosition,
      radioButtonWrap,
    };
  },
};
</script>
